import { Box, Dialog, DialogContent, DialogTitle, Grid, IconButton, InputAdornment, Typography } from '@mui/material';
import _ from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Dropdown } from 'react-bootstrap';
import { useHistory } from 'react-router-dom';
import { routeName } from 'routes/routes-name';

/** TRANSLATION*/
import { useTranslation } from 'react-i18next';

/**API */
import ClinicApi from 'api/master/clinic.api';
import ReportApi, { FindAllReportInterface } from 'api/report.api';
import { getBranch } from 'utils/app.utils';

/**COMPONENT */
import HeaderCard from 'component/CardCustom/HeaderCard';
import { useDownloadExcel } from 'component/Excel/hooks/useExcel';
import Loading from 'component/Loading';
import PatientList from './PatientReceiveServiceList';

/**STYLE */
import { faQuestionCircle, faSearch, faTimes } from '@fortawesome/free-solid-svg-icons';
import CloseIcon from '@mui/icons-material/Close';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import OperativeTypeApi from 'api/setting/treatments/operative-type.api';
import OperativeApi from 'api/setting/treatments/operative.api';
import InputSecondNewDatePicker, { PICKER_VIEWS } from 'component/Input/InputSecondNewDatePicker';
import FunctionPrint from 'component/Print/print';
import { SORT_TYPE } from 'constants/common';
import * as UseStyled from 'features/report/useStyled';
import moment from 'moment';
import MainButton from 'new-components/buttons/main-button';
import RadioButton from 'new-components/buttons/radio-button';
import SelectInput from 'new-components/inputs/select-input';
import SelectCheckBoxInput from 'new-components/inputs/select_checkbox_input';
import TextInput from 'new-components/inputs/text-input';

export default function PatientReceiveService() {
    const { t } = useTranslation();
    const history = useHistory();
    const componentRef = useRef<HTMLDivElement>(null);

    const [branchId, setBranchId] = useState<any>(getBranch());
    const [branches, setBranches] = useState<any[]>([]);
    const [rowCount, setRowCount] = useState<number>(0);
    const momentNow = moment().format('YYYY-MM-DD')
    const [date, setDate] = useState<string>(momentNow)
    const [sortBy, setSortBy] = useState<string>('patient_cases.createdAt')
    const [sortType, setSortType] = useState<SORT_TYPE>(SORT_TYPE.DESC)

    const [operativeTypes, setOperativeTypes] = useState<any>()
    const [operative, setOperative] = useState<any>()
    const [operativeTypeId, setOperativeTypeId] = useState<(string | number)[]>([])
    const [operativeId, setOperativeId] = useState<(string | number)[]>([])

    const [patientData, setPatientData] = useState<any[]>([]);
    const [patientDataExport, setPatientDataExport] = useState<any[]>([]);
    const [patientPage, setPatientPage] = useState<number>(1);
    const [patientLimitPage, setPatientLimitPage] = useState<number>(10);
    const [search, setSearch] = useState<string>();
    const [serviceStatus, setServiceStatus] = useState('LAST_SERVICE');

    const [year, setYear] = useState<any>(1);
    const [month, setMonth] = useState<any>(0);
    const [day, setDay] = useState<any>(0);
    const [fetch, setFetch] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(true);
    const [openInfo, setOpenInfo] = useState<boolean>(false);

    const verifyPermission = async () => {
        const condition: FindAllReportInterface = {
            page: 1, pageLimit: 200
        }
        ReportApi.reportList(condition)
            .then(({ data }) => {
                const check = _.find(data, (val: any) => String(val.reportKey) === 'PATIENT_RECEIVE_SERVICE') ? true : false
                if (!check) {
                    history.push(routeName.report)
                    return false
                }
            })

        ClinicApi.findAllBranches()
            .then(({ data }) => setBranches(data))
            .catch((e) => { return })

        OperativeTypeApi.findAll({ status: 'ACTIVE', pageLimit: 50 })
            .then(({ data }) => {
                setOperativeTypes(data ?? []);
            })
            .catch((e) => { return })


        return true
    }

    useEffect(() => {
        verifyPermission()
    }, [])

    const fetchPatientReceiveService = useCallback(async () => {
        await verifyPermission()
        let condition: FindAllReportInterface = {
            page: patientPage, pageLimit: patientLimitPage,
            branchId: branchId,
            date: date,
            month: month,
            year: year,
            day: day,
            serviceStatus: serviceStatus
        }
        if (search) condition = { ...condition, search: search }
        const listOperativeTypeId = _.filter(operativeTypeId, (v: any) => Number(v) > 0).join(',')
        if (listOperativeTypeId) condition = { ...condition, operativeTypeId: listOperativeTypeId }
        const listOperativeId = _.filter(operativeId, (v: any) => Number(v) > 0).join(',')
        if (listOperativeId) condition = { ...condition, operativeId: listOperativeId }
        if (sortBy) condition = { ...condition, sortBy: sortBy }
        if (sortType) condition = { ...condition, sortType: sortType }

        ReportApi.reportPatientReceiveService(condition)
            .then(({ headers, data }) => {
                setPatientData(data.rows)
                setRowCount(headers['x-total'])
            }).catch((e) => setLoading(false))
            .finally(() => setLoading(false))
        setFetch(false)

    }, [fetch, patientPage, patientLimitPage, sortBy, sortType, fetch])

    const fetchPatientReceiveServiceExport = useCallback(async () => {
        let condition: FindAllReportInterface = {
            page: 1, pageLimit: 1000,
            branchId: branchId,
            date: date,
            month: month,
            year: year,
            day: day,
            serviceStatus: serviceStatus
        }
        if (search) condition = { ...condition, search: search }
        const listOperativeTypeId = _.filter(operativeTypeId, (v: any) => Number(v) > 0).join(',')
        if (listOperativeTypeId) condition = { ...condition, operativeTypeId: listOperativeTypeId }
        const listOperativeId = _.filter(operativeId, (v: any) => Number(v) > 0).join(',')
        if (listOperativeId) condition = { ...condition, operativeId: listOperativeId }
        if (sortBy) condition = { ...condition, sortBy: sortBy }
        if (sortType) condition = { ...condition, sortType: sortType }

        ReportApi.reportPatientReceiveService(condition)
            .then(({ data }) => {
                setPatientDataExport(data.rows)
            }).catch((e) => setLoading(false))
            .finally(() => setLoading(false))

    }, [fetch, sortBy, sortType])

    useEffect(() => {
        const listOperativeTypeId = _.filter(operativeTypeId, (v: any) => Number(v) > 0).join(',')
        if (!listOperativeTypeId) {
            setOperative([])
            return
        }
        OperativeApi.findAll({ status: 'ACTIVE', pageLimit: 100, operativeTypeId: listOperativeTypeId })
            .then(({ data }) => {
                setOperative(data);
            })
            .catch((e) => {
                console.error('Error:', e);
            });

    }, [operativeTypeId])

    const { onDownload } = useDownloadExcel({
        tableExportRef: componentRef.current,
        filename: `${t(`REPORT.REPORT_NAME.PATIENT_RECEIVE_SERVICE`)}`,
        sheetName: `${t(`REPORT.REPORT_NAME.PATIENT_RECEIVE_SERVICE`)}`
    })

    const onRequestSort = (sortByVal: string, sortTypeVal: any) => {
        setSortType(sortTypeVal)
        setSortBy(sortByVal)
    }

    const getAllFilter = () => {
        const selectedOperativeIds = operativeId.filter((v: any) => Number(v) > 0)
        const selectedOperativeNames = _.filter(operative, (op: any) => selectedOperativeIds.includes(op.operativeId))
            .map((op: any) => op.operativeName || op.operativeNameEn)
            .join(', ')
        const selectedOperativeTypeIds = operativeTypeId.filter((v: any) => Number(v) > 0)
        const selectedOperativeTypeNames = _.filter(operativeTypes, (ot: any) => selectedOperativeTypeIds.includes(ot.operativeTypeId))
            .map((ot: any) => ot.operativeTypeName || ot.operativeTypeNameEn)
            .join(', ')
        return [{
            label: t('REPORT.FILTER.BRANCH'),
            value: _.get(_.find(branches, { branchId }), 'branchName') || _.get(_.find(branches, { branchId }), 'branchNameEn')
        },
        {
            label: t('REPORT.FILTER.TYPE_OPERATION'),
            value: selectedOperativeTypeNames || t('REPORT.FILTER.ALL')
        },
        {
            label: t('REPORT.FILTER.OPERATION'),
            value: selectedOperativeNames || t('REPORT.FILTER.ALL')
        },
        {
            label: t('REPORT.FILTER.YEAR'),
            value: year
        },
        {
            label: t('REPORT.FILTER.MONTH'),
            value: month
        }
        ]

    }
    const handleChangeRowsPerPage = (limit: number) => setPatientLimitPage(limit)

    const handleChangePage = (val: number) => setPatientPage(val)

    /** UseEffect */
    useEffect(() => {
        fetchPatientReceiveService()
    }, [fetchPatientReceiveService])

    useEffect(() => {
        fetchPatientReceiveServiceExport()
    }, [fetchPatientReceiveServiceExport])


    return (
        <Box>
            <HeaderCard
                text={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {t('REPORT.REPORT_NAME.PATIENT_RECEIVE_SERVICE')}
                        <Box
                            onClick={() => setOpenInfo(true)}
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.75,
                                px: 1.25,
                                py: 0.5,
                                ml: 1,
                                cursor: 'pointer',
                                color: '#2F4269',
                                backgroundColor: 'rgba(47, 66, 105, 0.08)',
                                borderRadius: '999px',
                                fontSize: '14px',
                                fontWeight: 500,
                                transition: 'background-color 0.15s ease',
                                '&:hover': {
                                    backgroundColor: 'rgba(47, 66, 105, 0.16)'
                                }
                            }}
                            role="button"
                            aria-label={t('รูปแบบการแสดงผล')}
                        >
                            <FontAwesomeIcon icon={faQuestionCircle} style={{ fontSize: 16 }} />
                            <Box component="span">{t('คำอธิบายรายงาน')}</Box>
                        </Box>
                    </Box>
                }
            />

            <Box sx={{ px: 3, pt: 2 }}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <RadioButton
                            name=""
                            onChange={e => setServiceStatus(e.target.value)}
                            options={[
                                {
                                    label: t('รายชื่อผู้ป่วยที่เข้ารับบริการตามช่วงเวลา'),
                                    value: 'LAST_SERVICE',
                                    tooltip: t('ระบบจะแสดงรายชื่อผู้ป่วยที่เข้ารับบริการภายในช่วง เวลา โดยนับจากวันที่กำหนด'),
                                },
                                {
                                    label: t('รายชื่อผู้ป่วยที่ขาดการรับบริการตามช่วงเวลา'),
                                    value: 'NO_SERVICE',
                                    tooltip: t('ระบบจะแสดงรายชื่อผู้ป่วยที่ไม่ได้เข้ารับบริการตาม ช่วงเวลา โดยนับจากวันที่กำหนด'),
                                }
                            ]}
                            row
                            toolTip
                            defaultValue="LAST_SERVICE"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3} xl={2}>
                        <TextInput
                            label={t('REPORT.REPORT_NAME.SEARCH_PATIENT_RECEIVE_SERVICE')}
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            fullWidth
                            inputProps={{
                                endAdornment: (
                                    <InputAdornment position="end" >
                                        {!search ? <FontAwesomeIcon icon={faSearch} style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: 15 }} /> :
                                            <FontAwesomeIcon
                                                icon={faTimes}
                                                onClick={() => {
                                                    setSearch('');
                                                }}
                                                style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: 17, cursor: 'pointer' }} />}
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3} xl={2}>
                        <SelectInput
                            label={t('REPORT.FILTER.BRANCH')}
                            value={branchId}
                            onChange={e => {
                                setBranchId(Number(e.target.value));
                                setPatientPage(1);
                            }}
                            options={branches.map(b => ({
                                value: b.branchId,
                                name: b.branchName || b.branchNameEn || ''
                            }))}
                            width="100%"
                        />
                    </Grid>

                    <Grid item xs={12} sm={6} md={3} xl={2}>
                        <SelectCheckBoxInput
                            label={t('DASHBOARD.OPERATIVE_TYPE')}
                            options={Array?.isArray(operativeTypes)
                                ? operativeTypes.map((o: any) => ({
                                    id: o.operativeTypeId,
                                    name: o.operativeTypeName || '',
                                }))
                                : []}
                            selected={operativeTypeId}
                            onChange={(ids: (string | number)[]) => {
                                setOperativeTypeId(ids);
                                setOperativeId([]);
                                setPatientPage(1);
                            }}
                            searchable
                            fullWidth
                            width="100%"
                        />
                    </Grid>

                    <Grid item xs={12} sm={6} md={3} xl={2}>
                        <SelectCheckBoxInput
                            label={t('DASHBOARD.HEADER.OPERATIVE')}
                            options={_.map(operative, (item: any) => ({
                                id: item.operativeId,
                                name: item.operativeName || '',
                            }))}
                            selected={operativeId}
                            onChange={(ids: (string | number)[]) => {
                                setOperativeId(ids);
                                setPatientPage(1);
                            }}
                            searchable
                            fullWidth
                            width="100%"
                            disabled={!operativeTypeId.length}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3} xl={2}>
                        <InputSecondNewDatePicker
                            dateFormat="DD/MM/YYYY"
                            placeholder={t('REPORT.FILTER.DATE')}
                            value={date}
                            onchange={(e: any) => setDate(moment(e).format('YYYY-MM-DD'))}
                            views={PICKER_VIEWS.DAY}
                            inputHeight={40}
                        />
                    </Grid>

                    <Grid item xs={6} sm={3} md={1} xl={1}>
                        <SelectInput
                            value={year}
                            onChange={e => {
                                setYear(Number(e.target.value));
                                setDay(1);
                                setPatientPage(1);
                            }}
                            options={Array.from({ length: 11 }, (_, i) => ({
                                value: i,
                                name: `${i} ${t('REPORT.FILTER.YEAR')}`
                            }))}
                            width="100%"
                        />
                    </Grid>
                    <Grid item xs={6} sm={3} md={1} xl={1}>
                        <SelectInput
                            value={month}
                            onChange={e => {
                                setMonth(Number(e.target.value));
                                setDay(1);
                                setPatientPage(1);
                            }}
                            options={Array.from({ length: 13 }, (_, i) => ({
                                value: i,
                                name: `${i} ${t('REPORT.FILTER.MONTH')}`
                            }))}
                            width="100%"
                        />
                    </Grid>

                    <Grid item xs={6} sm={3} md={1} xl={1}>
                        <SelectInput
                            value={day}
                            onChange={e => {
                                setDay(Number(e.target.value));
                                setPatientPage(1);
                            }}
                            options={Array.from({ length: 31 }, (_, i) => ({
                                value: i,
                                name: `${i} ${t('วัน')}`
                            }))}
                            width="100%"
                        />
                    </Grid>

                    <Grid item xs={6} sm={3} md={1} xl={1}>
                        <MainButton
                            title={t('INPUT.SEARCH')}
                            type="primary"
                            variant="contained"
                            onClick={() => {
                                setLoading(true);
                                setFetch(true)
                                setPatientPage(1);
                            }}
                            style={{ width: '120px' }}
                        />
                    </Grid>

                    <Grid item xs={12} md={5} xl={10} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <UseStyled.DropdownDocument>
                            <Dropdown.Toggle id="dropdown-basic">
                                {t('PRINT_DOCUMENT')}
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                                <Dropdown.Item onClick={() => {
                                    const container = componentRef.current;
                                    const restored: Array<() => void> = [];
                                    if (container) {
                                        const cells = container.querySelectorAll<HTMLTableCellElement>('td.excel-phone-text');
                                        cells.forEach((td) => {
                                            const original = td.textContent || '';
                                            const trimmed = original.trim();
                                            if (trimmed && /^[0-9]+$/.test(trimmed)) {
                                                const padded = '0'.repeat(trimmed.length);
                                                td.textContent = `=TEXT(${trimmed},"${padded}")`;
                                                restored.push(() => { td.textContent = original; });
                                            }
                                        });
                                    }
                                    onDownload();
                                    restored.forEach((fn) => fn());
                                }}>{t('Excel')}</Dropdown.Item>
                                <FunctionPrint
                                    content={() => componentRef.current}
                                    removeIfram
                                    trigger={() => <Dropdown.Item>{t('PDF')}</Dropdown.Item>}
                                    style="@page { size: auto; margin: 0.5cm; } @media print { body { -webkit-print-color-adjust: exact; }}"
                                />
                            </Dropdown.Menu>
                        </UseStyled.DropdownDocument>
                    </Grid>
                </Grid>
                <Box sx={{ mt: 3, pb: 4 }}>
                    <PatientList
                        filter={getAllFilter}
                        page={patientPage}
                        pageLimit={patientLimitPage}
                        rowCount={rowCount}
                        sortBy={sortBy}
                        sortType={sortType}
                        setSortBy={setSortBy}
                        setSortType={setSortType}
                        data={patientData}
                        exportData={patientDataExport}
                        onRequestSort={onRequestSort}
                        handleChangeRowsPerPage={handleChangeRowsPerPage}
                        handleChangePage={handleChangePage}
                        componentRef={componentRef}
                    />
                </Box>
            </Box>

            <Dialog
                open={openInfo}
                onClose={() => setOpenInfo(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: '8px' } }}
            >
                <DialogTitle
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        backgroundColor: 'rgba(47, 66, 105, 0.04)',
                        borderLeft: '4px solid #2F4269',
                        py: 1.5
                    }}
                >
                    <Typography fontSize="16px" fontWeight={600} color="#2F4269">
                        {t('รูปแบบการแสดงผล')}
                    </Typography>
                    <IconButton onClick={() => setOpenInfo(false)} size="small" sx={{ p: 0 }}>
                        <CloseIcon sx={{ width: '24px', height: '24px', color: '#808080' }} />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ pt: 2, pb: 3 }}>
                    <Typography fontSize="14px" fontWeight={400} color="#666666" mt={2} mb={1.5}>
                        {t('ผู้ใช้สามารถเลือกดูข้อมูลได้ 2 รูปแบบ ดังนี้')}
                    </Typography>

                    <Typography fontSize="14px" fontWeight={600} color="#2F4269">
                        {t('1. รายชื่อผู้ป่วยที่เข้ารับบริการตามช่วงเวลา')}
                    </Typography>
                    <Typography fontSize="14px" fontWeight={400} color="#666666" mb={0.5}>
                        {t('ระบบจะแสดงรายชื่อคนไข้ที่เข้ามาทำหัตถการเสร็จสิ้นแล้ว ภายในช่วงเวลาที่กำหนด โดยนับย้อนหลังจากวันที่ระบุ')}
                    </Typography>
                    <Typography fontSize="14px" fontWeight={400} color="#666666" fontStyle="italic" mb={1.5}>
                        {t('ตัวอย่าง: ตั้งค่าวันที่ = 1 พ.ค. 2569, ย้อนหลัง 6 เดือน → ระบบจะแสดงคนไข้ทุกคนที่เข้ารับบริการระหว่าง 1 พ.ย. 2568 – 1 พ.ค. 2569')}
                    </Typography>

                    <Typography fontSize="14px" fontWeight={600} color="#2F4269">
                        {t('2. รายชื่อผู้ป่วยที่ขาดการรับบริการตามช่วงเวลา')}
                    </Typography>
                    <Typography fontSize="14px" fontWeight={400} color="#666666" mb={0.5}>
                        {t('ระบบจะแสดงรายชื่อคนไข้ที่ไม่ได้กลับมาทำหัตถการเกินช่วงเวลาที่กำหนด โดยอ้างอิงจากวันที่เข้ารับบริการครั้งล่าสุดของแต่ละคน')}
                    </Typography>
                    <Typography fontSize="14px" fontWeight={400} color="#666666" fontStyle="italic">
                        {t('ตัวอย่าง: ตั้งค่าวันที่ = 1 พ.ค. 2569, ย้อนหลัง 1 ปี → ระบบจะแสดงคนไข้ที่ไม่ได้มาคลินิกตั้งแต่ก่อน 1 พ.ค. 2568 เป็นต้นไป')}
                    </Typography>
                </DialogContent>
            </Dialog>

            <Loading show={loading} type="fullLoading" />
        </Box>
    );
}